import axios from 'axios'
import Config from 'config'

const PAGE_SEARCH_MIN_CHARS = 3
const MAX_PAGES_PER_TYPE = 5
// Matches the backend's cap on users/get_mentionable/
const MAX_PEOPLE = 10
const MENTION_DEBOUNCE_MS = 250

// Files embedded in the editor are stored as workspace documents; the
// backend serves them from public, non-expiring URLs.
export const uploadFile = (file) => {
  const formData = new FormData()
  formData.append('name', file.name)
  formData.append('file', file)
  return axios.post(`${Config.network.apiUrl}documents/`, formData).then(({ data }) => ({
    url: data.file,
    name: data.name || file.name,
    size: file.size
  }))
}

export const fetchLinkPreview = url => (
  axios.get(`${Config.network.apiUrl}link-preview/`, { params: { url } }).then(({ data }) => data)
)

// Results are only valid for the workspace they were fetched in (switching
// workspace doesn't reload the app), and go stale as people join or pages
// get published, so the caches are dropped on either
const CACHE_TTL_MS = 5 * 60 * 1000
const peopleCache = new Map()
const pagesCache = new Map()
const pagesInFlight = new Map()
const pagesFailed = new Set()
let cacheWorkspace = null
let cacheCreated = 0

const checkCacheScope = () => {
  const workspace = String(axios.defaults.headers.common.Workspace || '')
  if (workspace !== cacheWorkspace || Date.now() - cacheCreated > CACHE_TTL_MS) {
    peopleCache.clear()
    pagesCache.clear()
    pagesInFlight.clear()
    pagesFailed.clear()
    cacheWorkspace = workspace
    cacheCreated = Date.now()
  }
}

const remember = (cache, key, value) => {
  cache.set(key, value)
  if (cache.size > 100) {
    cache.delete(cache.keys().next().value)
  }
}

const toPersonItem = user => ({
  id: `user:${user.id}`,
  group: 'People',
  kind: 'user',
  mentionId: String(user.id),
  title: user.name || user.email,
  description: user.name ? user.email : '',
  avatar: user.avatar,
  icon: (user.name || user.email || '?').charAt(0).toUpperCase()
})

// The people matching `key` without a request, or null. A cached result for
// a prefix of the query that came back under the cap is the complete match
// set, so the longer query can be filtered locally.
const cachedPeople = (key) => {
  if (peopleCache.has(key)) {
    return peopleCache.get(key)
  }
  for (let end = key.length - 1; end >= 0; end -= 1) {
    const users = peopleCache.get(key.slice(0, end))
    if (users && users.length < MAX_PEOPLE) {
      return users.filter(user => (
        (user.name || '').toLowerCase().includes(key) || (user.email || '').toLowerCase().includes(key)
      ))
    }
  }
  return null
}

// Failures resolve empty but aren't cached, so the next keystroke retries
const fetchPeople = (search, key) => (
  axios.get(`${Config.network.apiUrl}users/get_mentionable/`, { params: { search } })
    .then(({ data }) => {
      remember(peopleCache, key, data)
      return data
    })
    .catch(() => [])
)

// Page search goes through the RAG-backed endpoint, so it only runs for
// queries long enough to be meaningful (same rule as the top search box)
const needsPages = key => key.length >= PAGE_SEARCH_MIN_CHARS

const fetchPages = (search, key) => {
  if (!pagesInFlight.has(key)) {
    const request = axios.get(`${Config.network.apiUrl}search/simple-list/`, { params: { search, limit: MAX_PAGES_PER_TYPE } })
      .then(({ data }) => {
        const toItem = (kind, icon, description) => page => ({
          id: `${kind}:${page.id}`,
          group: 'Pages',
          kind,
          mentionId: String(page.id),
          title: page.title,
          description,
          icon
        })
        const pages = [
          ...(data.advices || []).map(toItem('advice', '📄', 'Advice')),
          ...(data.legal_changes || []).map(toItem('legalChange', '⚖', 'Legal change'))
        ]
        remember(pagesCache, key, pages)
        pagesFailed.delete(key)
        return pages
      })
      .catch(() => {
        pagesFailed.add(key)
        return []
      })
      .then((pages) => {
        pagesInFlight.delete(key)
        return pages
      })
    pagesInFlight.set(key, request)
  }
  return pagesInFlight.get(key)
}

const waitForTyping = signal => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, MENTION_DEBOUNCE_MS)
  if (signal) {
    signal.addEventListener('abort', () => {
      clearTimeout(timer)
      reject(new Error('aborted'))
    }, { once: true })
  }
})

// Whether page results for this query are still to come, i.e. the menu
// shouldn't claim nothing was found yet
export const mentionPagesPending = (query) => {
  const key = (query || '').trim().toLowerCase()
  return needsPages(key) && !pagesCache.has(key) && !pagesFailed.has(key)
}

// Resolves with the matches that can be shown now: people are never held
// back by the slower RAG page search. If pages were still pending, they are
// passed to `onPages` together with the people once they arrive.
// Debounced here rather than by the suggestion plugin so that whatever is
// known locally (the bare "@", backspacing, narrowing a short people list)
// shows without any delay.
export const fetchMentionItems = (query, signal, onPages) => {
  checkCacheScope()
  const search = (query || '').trim()
  const key = search.toLowerCase()
  const knownPeople = cachedPeople(key)
  const knownPages = needsPages(key) ? pagesCache.get(key) : []
  const combine = people => [...people.map(toPersonItem), ...(pagesCache.get(key) || [])]

  if (knownPeople && knownPages) {
    return Promise.resolve(combine(knownPeople))
  }

  // The bare "@" is the first thing typed, so it isn't worth delaying
  const wait = key ? waitForTyping(signal) : Promise.resolve()
  const pagesDone = knownPages ? Promise.resolve(null) : wait.then(() => fetchPages(search, key))
  const people = knownPeople ? Promise.resolve(knownPeople) : wait.then(() => fetchPeople(search, key))

  pagesDone
    .then(pages => pages && people.then((users) => {
      if (onPages && !(signal && signal.aborted)) {
        onPages(combine(users))
      }
    }))
    .catch(() => {})

  return knownPeople ? Promise.resolve(combine(knownPeople)) : people.then(combine)
}

export const MENTION_PAGE_MIN_CHARS = PAGE_SEARCH_MIN_CHARS

const pageEndpoints = {
  advice: 'advices',
  legalChange: 'legal-changes'
}

// Id and title for a page mention of an advice or legal change. The
// returned id can differ from the requested one: an advice that has since
// been edited and republished resolves to its current version.
export const fetchPageMention = (kind, id) => (
  axios.get(`${Config.network.apiUrl}${pageEndpoints[kind]}/${id}/`)
    .then(({ data }) => ({ id: String(data.id), title: data.title || data.act_name || '' }))
)
